import FullscreenIcon from "@mui/icons-material/Fullscreen";
import React, { createContext, useContext, useState } from "react";
import editSvg from "../../assets/new icons/edit.svg";
import trashSvg from "../../assets/new icons/trash.svg";
import { BASE_URL, businessunitName, pythonBaseUrl } from "../../App_Settings/app_settings";
import { v4 as uuidv4 } from "uuid";
import { Modal, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect } from "react";
import "../../App.css";
import "./constants.css";
import "./config.css";
import { ShieldAlert } from "lucide-react";
import { getUserIdFromLocalStorage, logFileProcess } from "../../API_Endpoints/APIEndpoints";
import NotificationService from "../../services/notificationService";
import { updateFileData, importFileData } from "../../services/fileDataService";
import { toast } from "react-toastify";

// Export NotificationService for reuse
export { NotificationService };

// Create a context
const SharedValueContext = createContext();

// Define a custom hook to use the shared value
export const useSharedValue = () => {
  return useContext(SharedValueContext);
};

// Create a provider component to wrap your app
export const SharedValueProvider = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const [isDirectNavigation, setIsDirectNavigation] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [dataRows, setDataRows] = useState([]);
  const [isChatWindowVisible, setIsChatWindowVisible] = useState(false);
  const [isNotificationWindowVisible, setIsNotificationWindowVisible] = useState(false);
  const [numberOfSelectedRows, setNumberOfSelectedRows] = useState(10);
  const [treeView, setTreeView] = useState("vertical");
  const [notifications, setNotifications] = useState([]);
  const [formsData, setFormsdata] = useState([]);
  const [withHolding, setWithHolding] = useState(null);
  const [confidence, setConfidence] = useState(100);
  const [defaultBusinessunit, setDefaultBusinessunit] = useState(null);
  const [defaultOrganization, setDefaultOrganization] = useState(null);
  const [defaultUser, setDefaultUser] = useState(null);
  const [businessUnitsList, setBusinessUnitsList] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [indexValue, setIndexValue] = useState(false);
  const [isPackProcessStarted, setIsPackProcessStarted] = useState(false);
  const [processStates, setProcessStates] = useState([]);
  const [withholdingData, setWithholdingData] = useState([]);
  const [isUpdatedExcel, setIsUpdatedExcel] = useState(false);
  //loading //completed
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [updatedrows, setUpdatedRows] = useState([]);
  const [successFilename, setSuccessFilename] = useState("");
  const [isSuccessPopup, setIsSuccessPopup] = useState(false);
  const [isRunProcessStarted, setIsRunProcessStarted] = useState(false);
  const [isTourCompleted, setIsTourCompleted] = useState(false);
  const [metricsList, setMetricsList] = useState([]);

  const updateDrawerMenu = (value) => {
    setIsMenuOpen(value);
  };

  const handleProcessingDialogue = (value) => {
    setIsProcessing(value);
  };
  const handleNotificationList = (value) => {
    setNotifications(Array.isArray(value) ? value : []);
  };
  const getDataRows = () => {
    const userDataJSON = localStorage.getItem("token");
    const userData = JSON.parse(userDataJSON);

    fetch(`${BASE_URL}/FileData/GetAllFiles/${userData.userId}`, {
      headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` },
    })
      .then((response) => response.json())
      .then((data) => {
        setDataRows(data.filter((row) => row.isProcessed === false));
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        // Stop loading in case of error
      });
  };
  const getBusinessUnits = async () => {
    try {
      const { getBusinessUnits: fetchBusinessUnits, getUserAssignedBusinessUnitIds } =
        await import("../../Utility/utility");
      const [data, assignedIds] = await Promise.all([
        fetchBusinessUnits(),
        getUserAssignedBusinessUnitIds(),
      ]);

      // Filter out deleted items first
      const activeBusinessUnits = data.filter((row) => row.isDeleted === false);

      // Scope to only BUs assigned to this user via their user profile (applies to every role, including Admin)
      const scopedBusinessUnits =
        assignedIds.size === 0
          ? activeBusinessUnits
          : activeBusinessUnits.filter((bu) => assignedIds.has(bu.id));

      // Create a Map to store unique business units by name
      // Duplicate names are allowed at the DB level (as long as instanceName differs),
      // so key by (name + instanceName) to keep them distinct in the dropdown.
      const uniqueNameMap = new Map();

      // Add the "All" option first
      const allOption = {
        name: `All - ${businessunitName}`,
        displayName: `All - ${businessunitName}`,
        id: "all",
      };
      uniqueNameMap.set(allOption.name, allOption);

      // Add each business unit — compute a displayName that includes the instance name when present
      scopedBusinessUnits.forEach((unit) => {
        if (unit && unit.name) {
          const instance = (unit.instanceName || "").trim();
          const displayName = instance ? `${unit.name} (${instance})` : unit.name;
          const key = `${unit.name}||${instance}`;
          if (!uniqueNameMap.has(key)) {
            uniqueNameMap.set(key, { ...unit, displayName });
          }
        }
      });

      // Convert back to array
      const uniqueBusinessUnits = Array.from(uniqueNameMap.values());

      setBusinessUnitsList(uniqueBusinessUnits);
    } catch (error) {
      console.error("Error fetching business units:", error);
    }
  };

  /**
   * Format a business unit's display label: "Name (InstanceName)" when instance is present,
   * otherwise just "Name". Reads name + instance directly from the row supplied by the API
   * (`businessUnit` + `businessUnitInstance` on file rows, `name` + `instance` on user
   * businessUnits[]). String input is also supported for the rare caller that only has a name.
   */
  const formatBusinessUnitLabel = (nameOrRow) => {
    if (!nameOrRow) return "";
    if (typeof nameOrRow === "object") {
      const name = nameOrRow.businessUnit || nameOrRow.name || "";
      const instance = String(
        nameOrRow.businessUnitInstance || nameOrRow.instance || nameOrRow.instanceName || ""
      ).trim();
      if (!name) return "";
      return instance ? `${name} (${instance})` : name;
    }
    return nameOrRow;
  };

  // Resolve a Business Unit's Id from its name using the loaded list.
  const getBusinessUnitIdByName = (name) => {
    if (!name) return null;
    const match = businessUnitsList.find(
      (bu) => (bu.name || "").trim().toLowerCase() === String(name).trim().toLowerCase()
    );
    return match && match.id && match.id !== "all" ? match.id : null;
  };
  const updateSearchValue = (value) => {
    setSearchValue(value);
  };
  const updateDisplayChatWindowValue = (value) => {
    setIsChatWindowVisible(value);
  };
  const updateNotificationWindowValue = (value) => {
    setIsNotificationWindowVisible(value);
  };
  const updateDirectNavigation = (value) => {
    setIsDirectNavigation(value);
  };
  const updateRowNumberOfPages = (value) => {
    setNumberOfSelectedRows(value);
  };
  const handleTreeView = (value) => {
    setTreeView(value);
  };
  const handleIsRunProcessStarted = (value) => {
    setIsRunProcessStarted(value);
  };
  const handleIsSuccessPopup = (value) => {
    setIsSuccessPopup(value);
  };
  const handleSelectedRows = (value) => {
    setSelectedRows(value);
  };

  const handleTourCompleted = (value) => {
    if (value === false) {
      setIsTourCompleted(false);
    } else {
      localStorage.setItem("tourCompleted", "true");
      setIsTourCompleted(value);
    }
  };
  const handleCheckTourCompleted = () => {
    const tourStatus = localStorage.getItem("tourCompleted");

    if (tourStatus === "true") {
      setIsTourCompleted(true);
    }
  };

  const handleTourGuide = () => {
    localStorage.setItem("tourCompleted", "false");
    setIsTourCompleted(false);
  };
  const generateUUID = () => {
    const newUUID = uuidv4(); // Generate a new UUID
    return newUUID;
  };

  const handleExtractionStatus = async (
    requestId,
    selectedRowFromRows,
    businessUnit = null,
    parentId = null,
    pollAttempt = 1,
    mainRequestId = null
  ) => {
    const formData = new FormData();
    // Use selectedRowFromRows.businessUnit if it exists and isn't empty, otherwise use the provided businessUnit parameter
    const effectiveBusinessUnit =
      !selectedRowFromRows.businessUnit || selectedRowFromRows.businessUnit.trim() === ""
        ? businessUnit
        : selectedRowFromRows.businessUnit;
    const jwtUser = getUserIdFromLocalStorage();
    formData.append("business_unit", effectiveBusinessUnit);
    formData.append(
      "organization_id",
      selectedRowFromRows.organizationId || jwtUser.organizationId || ""
    );
    formData.append("user_id", selectedRowFromRows.userID || jwtUser.userId || "");
    formData.append("corelational_id", generateUUID());
    formData.append("is_multiple_whs", parentId ? true : false);
    formData.append("original_filename", selectedRowFromRows.fileName);
    formData.append("request_id", String(requestId));
    formData.append("upload_type", String(selectedRowFromRows.uploadType || 1));
    formData.append("filename", String(requestId));
    formData.append("poll_attempt", String(pollAttempt));

    const url = `${pythonBaseUrl}/Extract/Azure-Extraction-Status`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const jsonData = await response.json();
        if (jsonData.status == "DONE") {
          const evd = jsonData.extractedValidationData;
          const isWhsEmpty =
            evd &&
            (!evd.businessUnitList || evd.businessUnitList.length === 0) &&
            (!evd.incomeCodesList || evd.incomeCodesList.length === 0) &&
            (!evd.accountsList || evd.accountsList.length === 0) &&
            (!evd.accountConnectionsList || evd.accountConnectionsList.length === 0);

          // Single-file extraction (mainRequestId is null): skip DB save entirely when empty
          if (isWhsEmpty && !mainRequestId) return "WHS_EMPTY";

          setWithholdingData(evd);
          const updatedRowData = {
            ...selectedRowFromRows,
            isProcessed: true,
            parentId: parentId,
            status: 2, // Status 2: Extraction complete
            formsData: parentId ? JSON.stringify(evd.businessUnitList) : null,
            numberOfFields: intValue(jsonData.valued_field_count),
            dataExtraction: JSON.stringify(jsonData.tableData), // Stringify the list
            dataValidation: JSON.stringify(evd), // Stringify the list
            dataVisualisation: JSON.stringify(jsonData.treeData),
            accuracyPercentage: 100,
            withholdingData: new Date(),
            confidenceLevel:
              jsonData.confidence_score < 1
                ? Math.round(jsonData.confidence_score * 100)
                : Math.round(jsonData.confidence_score),
          };
          setConfidence(
            jsonData.confidence_score < 1
              ? Math.round(jsonData.confidence_score * 100)
              : Math.round(jsonData.confidence_score)
          );

          fetch(`${BASE_URL}/FileData/ImportFiles`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
            },
            body: JSON.stringify(updatedRowData),
            // Include the FormData object as the request body
          }).then((response) => {
            if (response) {
            }
          });

          // Pack extraction: save data to DB first, then signal empty
          if (isWhsEmpty) return "WHS_EMPTY";
        }
        return jsonData.status; // Return the status value
      } else {
        // console.error(`Azure AI API call failed for requestId: ${requestId}`);
        return "FAILED";
      }
    } catch (error) {
      console.error("Error in handleExtractionStatus:", error);
      return "FAILED";
    }
  };

  const pollFileStatus = async (
    requestId,
    selectedRowFromRows,
    businessUnit = null,
    parentId = null,
    mainRequestId = null
  ) => {
    return new Promise((resolve, reject) => {
      let pollAttempt = 0; // Initialize polling attempt counter

      const checkStatus = async () => {
        try {
          pollAttempt++; // Increment attempt counter each time we poll

          const status = await handleExtractionStatus(
            requestId,
            selectedRowFromRows,
            businessUnit,
            parentId,
            pollAttempt,
            mainRequestId
          );

          if (status === "DONE") {
            clearInterval(intervalId);
            resolve(status);
          }
          if (status === "FAILED") {
            clearInterval(intervalId);
            resolve(status);
          }
          if (status === "WHS_EMPTY") {
            clearInterval(intervalId);
            resolve(status);
          }
        } catch (error) {
          console.error("Status check error:", error);
          clearInterval(intervalId);
          reject(error);
        }
      };

      // Initial immediate check
      checkStatus();

      // Check every 5 seconds
      const intervalId = setInterval(checkStatus, 10000);

      // Optional: Add timeout to prevent infinite polling
      setTimeout(
        () => {
          clearInterval(intervalId);
          reject(new Error("Status check timed out"));
        },
        10 * 60 * 1000
      ); // 10 minutes timeout
    });
  };
  // Function to add a new document to process
  const addDocumentToProcess = (doc, label) => {
    setProcessStates((prevStates) => [
      ...prevStates,
      {
        docPath: doc.output_path,
        status: "loading",
        label: `${label}`,
      },
    ]);
  };
  // Function to update status of a specific document by docPath
  const updateDocumentStatus = (docPath, newStatus, label) => {
    setProcessStates((prevStates) =>
      prevStates.map((doc) =>
        doc.docPath === docPath ? { ...doc, status: newStatus, label: label } : doc
      )
    );
  };

  useEffect(() => {
    if (
      withHolding &&
      withHolding !== null &&
      formsData &&
      formsData.length > 0 &&
      formsData !== null
    ) {
      const combinedData = {
        formsData: formsData,
        withholdingData: withholdingData,
      };
      const updatedRowData = {
        ...withHolding,
        // notes:JSON.stringify(withholdingData),
        confidenceLevel: confidence,
        formsData: JSON.stringify(combinedData),
      };

      updateFileData(updatedRowData).catch((error) => {
        console.error("Error updating file data:", error);
      });
    }
  }, [formsData, withHolding]);

  const getFormTypeDisplayName = (formType) => {
    if (!formType) return "";

    switch (formType.toLowerCase()) {
      case "w8imy":
        return "W-8IMY";
      case "w8bene":
        return "W-8BEN-E";
      case "w9":
        return "W-9";
      case "w8ben":
        return "W-8BEN";
      case "w8exp":
        return "W-8EXP";
      case "w8eci":
        return "W-8ECI";
      default:
        return formType; // Return the original if not matched
    }
  };

  const DataExtractProcess = async (rows) => {
    if (selectedRows && selectedRows.length >= 1) {
      setIsRunProcessStarted(true);
      let currentIndex = 0;

      const processNextItem = async () => {
        if (currentIndex >= selectedRows.length) {
          // All items processed
          setIsRunProcessStarted(false);
          return;
        }

        const rowId = selectedRows[currentIndex];
        const selectedRowFromRows = rows.find((row) => row.fileImportId === rowId);

        if (!selectedRowFromRows) {
          // Skip this item if not found
          currentIndex++;
          processNextItem();
          return;
        }

        setSelectedRow(selectedRowFromRows);
        setIndexValue(currentIndex);

        try {
          const formData = new FormData();
          formData.append("filename", selectedRowFromRows.fileImportId);
          formData.append("original_filename", selectedRowFromRows.fileName);
          formData.append("request_id", selectedRowFromRows.fileImportId);
          formData.append("extension", selectedRowFromRows.fileName.split(".").pop());
          const jwtUser = getUserIdFromLocalStorage();
          formData.append(
            "organization_id",
            selectedRowFromRows.organizationId || jwtUser.organizationId || ""
          );
          formData.append("user_id", selectedRowFromRows.userID || jwtUser.userId || "");
          formData.append("business_unit", selectedRowFromRows.businessUnit || "Demo");
          formData.append("upload_type", String(selectedRowFromRows.uploadType || 1));

          // Handle Third Party Forms (uploadType 13)
          if (selectedRowFromRows.uploadType === 13) {
            try {
              // Call Map-Excel-To-Template API
              const mappingFormData = new FormData();
              mappingFormData.append("file_id", selectedRowFromRows.filePath);
              const jwtUserMapping = getUserIdFromLocalStorage();
              mappingFormData.append(
                "organization_id",
                selectedRowFromRows.organizationId || jwtUserMapping.organizationId || ""
              );
              mappingFormData.append(
                "user_id",
                selectedRowFromRows.userID || jwtUserMapping.userId || ""
              );
              mappingFormData.append("business_unit", selectedRowFromRows.businessUnit || "Demo");

              // Add AltVendorID values if present in the row
              if (selectedRowFromRows.altVendorID1)
                mappingFormData.append("alt_vendor_id_1", selectedRowFromRows.altVendorID1);
              if (selectedRowFromRows.altVendorID2)
                mappingFormData.append("alt_vendor_id_2", selectedRowFromRows.altVendorID2);
              if (selectedRowFromRows.altVendorID3)
                mappingFormData.append("alt_vendor_id_3", selectedRowFromRows.altVendorID3);

              const mappingUrl = `${pythonBaseUrl}/Excel/Map-Excel-To-Template`;

              const mappingResponse = await fetch(mappingUrl, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
                },
                body: mappingFormData,
                mode: "cors",
              });

              if (mappingResponse.ok) {
                const mappingData = await mappingResponse.json();

                // Update file as processed with extracted data
                const updatedRowData = {
                  ...selectedRowFromRows,
                  isProcessed: true,
                  notes: "Deloitte Excel mapped successfully",
                  // numberOfFields: intValue(mappingData.valued_field_count),
                  dataExtraction: JSON.stringify(mappingData.tableData),
                  dataValidation: JSON.stringify(mappingData.extractedValidationData),
                  dataVisualisation: JSON.stringify(mappingData.treeData),
                  // accuracyPercentage: 100,
                  // withholdingData: new Date(),
                  status: 2, // Status 2: Extraction complete
                };

                const userToken = getUserIdFromLocalStorage().token;
                const updateResponse = await fetch(`${BASE_URL}/FileData/ImportFiles`, {
                  method: "PUT",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${userToken}`,
                  },
                  body: JSON.stringify(updatedRowData),
                });

                if (updateResponse.ok) {
                  setNotifications((prev) => [
                    ...(Array.isArray(prev) ? prev : []),
                    NotificationService.extractionSuccess(selectedRowFromRows.fileName, 1),
                  ]);

                  setSuccessFilename(selectedRowFromRows.fileName);
                  setIsSuccessPopup(true);
                } else {
                  setNotifications((prev) => [
                    ...(Array.isArray(prev) ? prev : []),
                    NotificationService.systemError(
                      "Database Save Failed",
                      `Failed to save mapping results for ${selectedRowFromRows.fileName}`,
                      `Status: ${updateResponse.status}`
                    ),
                  ]);
                }
              } else {
                const errorText = await mappingResponse.text();
                console.error("❌ Deloitte Excel mapping failed:", errorText);
                setNotifications((prev) => [
                  ...(Array.isArray(prev) ? prev : []),
                  NotificationService.extractionError(
                    selectedRowFromRows.fileName,
                    `Mapping failed: ${errorText}`
                  ),
                ]);
              }
            } catch (error) {
              console.error("💥 Error in Deloitte Excel mapping:", error);
              setNotifications((prev) => [
                ...(Array.isArray(prev) ? prev : []),
                NotificationService.extractionError(
                  selectedRowFromRows.fileName,
                  `Error during mapping: ${error.message}`
                ),
              ]);
            }

            // Move to next item after processing uploadType 13
            currentIndex++;
            processNextItem();
            return;
          } else if (selectedRowFromRows.uploadType >= 2 && selectedRowFromRows.uploadType <= 10) {
            const docType = getDocumentTypeByUploadType(selectedRowFromRows.uploadType);

            // Call uploadExtraction API for Single Form
            const extractionFormData = new FormData();
            extractionFormData.append("filename", selectedRowFromRows.fileImportId);
            extractionFormData.append("request_id", selectedRowFromRows.fileImportId);
            extractionFormData.append("extension", selectedRowFromRows.fileName.split(".").pop());
            extractionFormData.append(
              "filepath",
              `${selectedRowFromRows.organizationId}/${
                selectedRowFromRows.userID
              }/uploadfiles/${selectedRowFromRows.fileImportId}.pdf`
            );
            const jwtUserSingleForm = getUserIdFromLocalStorage();
            extractionFormData.append(
              "organization_id",
              selectedRowFromRows.organizationId || jwtUserSingleForm.organizationId || ""
            );
            extractionFormData.append("original_filename", selectedRowFromRows.fileName);
            extractionFormData.append(
              "user_id",
              selectedRowFromRows.userID || jwtUserSingleForm.userId || ""
            );
            extractionFormData.append("business_unit", selectedRowFromRows.businessUnit || "Demo");
            extractionFormData.append("doc_type", docType);
            extractionFormData.append("is_single_form", "true");
            extractionFormData.append("upload_type", String(selectedRowFromRows.uploadType || 2));

            const extractionUrl = `${pythonBaseUrl}/Extract/UploadExtraction`;

            try {
              const extractionResponse = await fetch(extractionUrl, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
                },
                body: extractionFormData,
                mode: "cors",
              });

              if (extractionResponse.ok) {
                const extractionData = await extractionResponse.json();

                // Report data is now stored directly by Python - no need to store from React
                if (extractionData.status === "success") {
                  // Extract confidence score from response
                  let confidenceLevel = null;
                  if (extractionData.confidence_score !== undefined) {
                    confidenceLevel =
                      extractionData.confidence_score < 1
                        ? Math.round(extractionData.confidence_score * 100)
                        : Math.round(extractionData.confidence_score);
                  } else if (extractionData.reportdata?.confidenceScore !== undefined) {
                    confidenceLevel =
                      extractionData.reportdata.confidenceScore < 1
                        ? Math.round(extractionData.reportdata.confidenceScore * 100)
                        : Math.round(extractionData.reportdata.confidenceScore);
                  }

                  // Only extract total pages and total forms if they don't already exist
                  let totalPages = selectedRowFromRows.totalPages;
                  let totalForms = selectedRowFromRows.totalForms;

                  // Only update if current values are null/undefined
                  if (extractionData.reportdata) {
                    if (
                      (totalPages === null || totalPages === undefined) &&
                      extractionData.reportdata.totalPages !== undefined
                    ) {
                      totalPages = Number(extractionData.reportdata.totalPages);
                    }
                    if (
                      (totalForms === null || totalForms === undefined) &&
                      extractionData.reportdata.totalForms !== undefined
                    ) {
                      // For single forms, apply WHS logic: 0 for WHS (uploadType 1), otherwise use response value
                      totalForms = Number(extractionData.reportdata.totalForms);
                    }
                  }

                  const updatedRowData = {
                    ...selectedRowFromRows,
                    formsData: JSON.stringify(extractionData.data),
                    isProcessed: true,
                    confidenceLevel: confidenceLevel,
                    totalPages: totalPages,
                    totalForms: totalForms,
                    status: 2, // Status 2: Extraction complete
                  };

                  const userToken = getUserIdFromLocalStorage().token;

                  const updateResponse = await fetch(`${BASE_URL}/FileData/ImportFiles`, {
                    method: "PUT",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${userToken}`,
                    },
                    body: JSON.stringify(updatedRowData),
                  });

                  if (updateResponse.ok) {
                    setNotifications((prev) => [
                      ...prev,
                      NotificationService.extractionSuccess(selectedRowFromRows.fileName, 1),
                    ]);

                    // Show success popup same as other upload types
                    setSuccessFilename(selectedRowFromRows.fileName);
                    setIsSuccessPopup(true);
                  } else {
                    const errorText = await updateResponse.text();

                    setNotifications((prev) => [
                      ...prev,
                      NotificationService.systemError(
                        "Database Save Failed",
                        `Failed to save extraction results for ${selectedRowFromRows.fileName}`,
                        `Status: ${updateResponse.status}`
                      ),
                    ]);
                  }
                } else {
                  setNotifications((prev) => [
                    ...prev,
                    NotificationService.extractionError(
                      selectedRowFromRows.fileName,
                      "Single Form extraction status failed."
                    ),
                  ]);
                }
              } else {
                console.error("❌ Single Form extraction upload failed");
                setNotifications((prev) => [
                  ...prev,
                  NotificationService.extractionError(
                    selectedRowFromRows.fileName,
                    "Single Form extraction upload failed."
                  ),
                ]);
              }
            } catch (error) {
              console.error("💥 Error in Single Form processing:", error);
              setNotifications((prev) => [
                ...prev,
                NotificationService.fileProcessingError(
                  selectedRowFromRows.fileName,
                  "Single Form processing failed. Please try again."
                ),
              ]);
            }

            // Move to next item
            currentIndex++;
            processNextItem();
            return;
          }

          const url = `${pythonBaseUrl}/${
            selectedRowFromRows.uploadType === 11 || selectedRowFromRows.uploadType === 14
              ? "Split/AzureSplitExtract"
              : "Extract/Azure-Upload-File"
          }`;

          // Call the Azure AI API
          const response = await fetch(url, {
            method: "POST",
            body: formData,
            headers: {
              Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
            },
            mode: "cors",
          });

          if (selectedRowFromRows.uploadType === 14) {
            // ── Forms Pack: split → extract each form → create new DB record per form ──
            setProcessStates([]);
            setIsPackProcessStarted(true);
            setIsProcessing(true);
            setIsSuccessPopup(false);

            addDocumentToProcess({ output_path: "Splitting Forms Pack" }, "Splitting Forms Pack");

            try {
              const initialSplitResponse = await response.json();

              // Async job pattern: if the backend returned a job_id, poll for the result
              let splitResponse = initialSplitResponse;
              if (initialSplitResponse.status === "PROCESSING") {
                const pollJobId = selectedRowFromRows.fileImportId;
                splitResponse = await new Promise((resolve, reject) => {
                  const timeout = setTimeout(
                    () => {
                      clearInterval(poll);
                      reject(new Error("Split job timed out after 20 minutes"));
                    },
                    20 * 60 * 1000
                  );

                  const poll = setInterval(async () => {
                    try {
                      const statusFormData = new FormData();
                      statusFormData.append("job_id", pollJobId);
                      const statusRes = await fetch(
                        `${pythonBaseUrl}/Split/AzureSplitExtractStatus`,
                        {
                          method: "POST",
                          headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` },
                          body: statusFormData,
                        }
                      );
                      const statusJson = await statusRes.json();
                      if (statusJson.status === "DONE") {
                        clearInterval(poll);
                        clearTimeout(timeout);
                        resolve(statusJson.result);
                      } else if (statusJson.status === "FAILED") {
                        clearInterval(poll);
                        clearTimeout(timeout);
                        reject(new Error(statusJson.error || "Split job failed"));
                      }
                      // PROCESSING — keep polling
                    } catch (e) {
                      clearInterval(poll);
                      clearTimeout(timeout);
                      reject(e);
                    }
                  }, 5000); // poll every 5 s
                });
              }

              if (splitResponse.status === "success") {
                const classifiedDocuments = splitResponse.classified_documents || [];
                setMetricsList(classifiedDocuments);
                updateDocumentStatus(
                  "Splitting Forms Pack",
                  "complete",
                  "Forms Pack Split Completed"
                );

                if (classifiedDocuments.length === 0) {
                  setIsPackProcessStarted(false);
                  setIsProcessing(false);
                  currentIndex++;
                  processNextItem();
                  return;
                }

                const { mapDocumentTypeToUploadType } =
                  await import("../../Controller/fileController.tsx");

                const BATCH_SIZE = 30;
                const extractionResults = [];
                for (
                  let batchStart = 0;
                  batchStart < classifiedDocuments.length;
                  batchStart += BATCH_SIZE
                ) {
                  const batch = classifiedDocuments.slice(batchStart, batchStart + BATCH_SIZE);

                  // Register only this batch's placeholders so the UI shows progress batch by batch
                  batch.forEach((doc, batchIdx) => {
                    const i = batchStart + batchIdx;
                    const isLowConfidence =
                      doc.confidence !== undefined &&
                      doc.confidence !== null &&
                      doc.confidence < 0.5;
                    const docType = isLowConfidence
                      ? "Unidentified Document Type"
                      : getFormTypeDisplayName(doc.doc_type) || doc.doc_type;
                    addDocumentToProcess(
                      { output_path: `form-pack-${i}-${doc.output_path}` },
                      `Extracting ${docType} ${i + 1}`
                    );
                  });

                  const batchResults = await Promise.all(
                    batch.map(async (doc, batchIdx) => {
                      const i = batchStart + batchIdx;
                      const docType = getFormTypeDisplayName(doc.doc_type) || doc.doc_type;
                      const docLabel = `${docType} ${i + 1}`;

                      try {
                        const mappedType = mapDocumentTypeToUploadType(doc.doc_type);
                        // Never assign uploadType 14 (Forms Pack) to individual extracted forms
                        const formUploadType = mappedType && mappedType !== 14 ? mappedType : 2;

                        // Skip extraction entirely for low-confidence classifications — don't
                        // waste a backend call on a page the classifier isn't confident about.
                        // Refer to it as an unidentified document type rather than naming the
                        // (unreliable) guessed form type.
                        if (
                          doc.confidence !== undefined &&
                          doc.confidence !== null &&
                          doc.confidence < 0.5
                        ) {
                          const unidentifiedLabel = `Unidentified Document Type ${i + 1}`;
                          updateDocumentStatus(
                            `form-pack-${i}-${doc.output_path}`,
                            "error",
                            `${unidentifiedLabel} not processed`
                          );
                          setNotifications((prev) => [
                            ...prev,
                            NotificationService.extractionError(
                              unidentifiedLabel,
                              `Classification confidence (${Math.round(doc.confidence * 100)}%) is below the 50% minimum required for extraction.`
                            ),
                          ]);
                          return { success: false };
                        }

                        // Extract the form using the same UploadExtraction flow as single forms
                        const formRequestId = generateUUID();
                        const extractionFormData = new FormData();
                        extractionFormData.append("filename", formRequestId);
                        extractionFormData.append("request_id", formRequestId);
                        extractionFormData.append("extension", "pdf");
                        extractionFormData.append("filepath", doc.file_url || doc.output_path);
                        const jwtUserFormsPack = getUserIdFromLocalStorage();
                        extractionFormData.append(
                          "organization_id",
                          selectedRowFromRows.organizationId ||
                            jwtUserFormsPack.organizationId ||
                            ""
                        );
                        extractionFormData.append(
                          "original_filename",
                          `${selectedRowFromRows.fileName.replace(".pdf", "")}_${docLabel}.pdf`
                        );
                        extractionFormData.append(
                          "user_id",
                          selectedRowFromRows.userID || jwtUserFormsPack.userId || ""
                        );
                        extractionFormData.append(
                          "business_unit",
                          selectedRowFromRows.businessUnit || "Demo"
                        );
                        extractionFormData.append("doc_type", doc.doc_type);
                        extractionFormData.append("is_single_form", "true");
                        extractionFormData.append("upload_type", String(formUploadType));
                        // Pass classification confidence so backend can reject low-confidence pages
                        if (doc.confidence !== undefined && doc.confidence !== null) {
                          extractionFormData.append(
                            "classification_confidence",
                            String(doc.confidence)
                          );
                        }

                        const extractionUrl = `${pythonBaseUrl}/Extract/UploadExtraction`;
                        const extractionResponse = await fetch(extractionUrl, {
                          method: "POST",
                          headers: {
                            Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
                          },
                          body: extractionFormData,
                          mode: "cors",
                        });

                        if (extractionResponse.ok) {
                          const extractionData = await extractionResponse.json();

                          if (extractionData.status === "success") {
                            let confidenceLevel = null;
                            const rawConfidence =
                              extractionData.confidence_score ??
                              extractionData.reportdata?.confidenceScore;
                            if (rawConfidence !== undefined && rawConfidence !== null) {
                              confidenceLevel =
                                rawConfidence < 1
                                  ? Math.round(rawConfidence * 100)
                                  : Math.round(rawConfidence);
                            }

                            // Create a new DB record for this individual form
                            const newFormRow = {
                              fileImportId: formRequestId,
                              userId: selectedRowFromRows.userID,
                              fileName: `${selectedRowFromRows.fileName.replace(".pdf", "")}_${docLabel}.pdf`,
                              filePath: doc.file_url || doc.output_path || "",
                              prcoessIdentifier: "AZURE-AI",
                              isProcessed: true,
                              businessUnit: selectedRowFromRows.businessUnit,
                              businessUnitID:
                                selectedRowFromRows.businessUnitID ||
                                selectedRowFromRows.businessUnitId,
                              businessUnitInstance: selectedRowFromRows.businessUnitInstance,
                              businessUnitAdminUrl: selectedRowFromRows.businessUnitAdminUrl,
                              uploadedBy: selectedRowFromRows.uploadedBy || "",
                              uploadedDate: new Date().toISOString(),
                              reviewedBy: null,
                              reviewedDate: null,
                              numberOfFields: null,
                              confidenceLevel: confidenceLevel,
                              approvedBy: null,
                              approvedDate: null,
                              emailedTo: null,
                              emailedDate: null,
                              isInvestment: false,
                              isWithholding: true,
                              numberOfRows: null,
                              numberOfTiers: null,
                              uploadType: formUploadType,
                              status: 2, // Extraction complete
                              rejectedBy: null,
                              rejectedDate: null,
                              accuracyPercentage: null,
                              rejectionReason: "",
                              notes: `Extracted from Forms Pack: ${selectedRowFromRows.fileName}`,
                              totalPages:
                                extractionData.reportdata?.totalPages || doc.pages?.length || 1,
                              totalForms: 1,
                              formsData: JSON.stringify(extractionData.data),
                              // parentId: selectedRowFromRows.fileImportId,

                              organizationId: selectedRowFromRows.organizationId,
                            };

                            await importFileData([newFormRow]);

                            updateDocumentStatus(
                              `form-pack-${i}-${doc.output_path}`,
                              "complete",
                              `${docLabel} Extracted`
                            );

                            setNotifications((prev) => [
                              ...prev,
                              NotificationService.extractionSuccess(newFormRow.fileName, 1),
                            ]);

                            return { success: true };
                          } else {
                            updateDocumentStatus(
                              `form-pack-${i}-${doc.output_path}`,
                              "error",
                              `${docLabel} Extraction Failed`
                            );
                            return { success: false };
                          }
                        } else {
                          updateDocumentStatus(
                            `form-pack-${i}-${doc.output_path}`,
                            "error",
                            `${docLabel} Extraction Failed`
                          );
                          return { success: false };
                        }
                      } catch (docError) {
                        console.error(`Error extracting form ${docLabel}:`, docError);
                        updateDocumentStatus(
                          `form-pack-${i}-${doc.output_path}`,
                          "error",
                          `${docLabel} extraction failed`
                        );
                        return { success: false };
                      }
                    })
                  );
                  extractionResults.push(...batchResults);
                }

                const allSucceeded = extractionResults.every((r) => r.success);

                // Delete the original Forms Pack file after all forms processed
                try {
                  const userToken = getUserIdFromLocalStorage().token;
                  await fetch(
                    `${BASE_URL}/FileData/ImportFiles/${selectedRowFromRows.fileImportId}`,
                    {
                      method: "DELETE",
                      headers: { Authorization: `Bearer ${userToken}`, accept: "*/*" },
                    }
                  );
                } catch (delErr) {
                  console.warn("Could not delete original Forms Pack record:", delErr);
                }

                if (allSucceeded) {
                  setSuccessFilename(selectedRowFromRows.fileName);
                  setIsSuccessPopup(true);
                }
              } else {
                throw new Error("Forms Pack split failed");
              }
            } catch (error) {
              console.error("Error processing Forms Pack:", error);
              setNotifications((prev) => [
                ...prev,
                NotificationService.fileProcessingError(
                  selectedRowFromRows.fileName,
                  "Forms Pack processing failed. Please try again."
                ),
              ]);
            } finally {
              // Allow React to render all final "complete" statuses before closing the dialog
              await new Promise((resolve) => setTimeout(resolve, 1000));
              setIsPackProcessStarted(false);
              setIsProcessing(false);
              currentIndex++;
              processNextItem();
            }
            return;
          } else if (selectedRowFromRows.uploadType === 11) {
            // Reset states for pack processing
            setProcessStates([]);
            setFormsdata([]);
            setIsPackProcessStarted(true);
            const processedWithholdingFiles = [];
            setWithholdingData([]);
            const formTypeCounts = {};
            setMetricsList([]);
            setIsUpdatedExcel(false);
            setIsProcessing(true);
            setIsSuccessPopup(false);
            setWithHolding(null);
            let excelFileName;
            let statistics;

            addDocumentToProcess({ output_path: "Splitting Pack" }, "Splitting Pack");

            try {
              const initialSplitResponse = await response.json();

              let splitResponse = initialSplitResponse;
              if (initialSplitResponse.status === "PROCESSING") {
                const pollJobId = selectedRowFromRows.fileImportId;
                splitResponse = await new Promise((resolve, reject) => {
                  const timeout = setTimeout(
                    () => {
                      clearInterval(poll);
                      reject(new Error("Split job timed out after 20 minutes"));
                    },
                    20 * 60 * 1000
                  );

                  const poll = setInterval(async () => {
                    try {
                      const statusFormData = new FormData();
                      statusFormData.append("job_id", pollJobId);
                      const statusRes = await fetch(
                        `${pythonBaseUrl}/Split/AzureSplitExtractStatus`,
                        {
                          method: "POST",
                          headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` },
                          body: statusFormData,
                        }
                      );
                      const statusJson = await statusRes.json();
                      if (statusJson.status === "DONE") {
                        clearInterval(poll);
                        clearTimeout(timeout);
                        resolve(statusJson.result);
                      } else if (statusJson.status === "FAILED") {
                        clearInterval(poll);
                        clearTimeout(timeout);
                        reject(new Error(statusJson.error || "Split job failed"));
                      }
                    } catch (e) {
                      clearInterval(poll);
                      clearTimeout(timeout);
                      reject(e);
                    }
                  }, 5000);
                });
              }

              // Report data is now stored directly by Python - no need to store from React

              if (splitResponse.status === "success") {
                statistics = splitResponse.statistics;
                const classifiedDocuments = splitResponse.classified_documents || [];

                setMetricsList(classifiedDocuments);
                updateDocumentStatus("Splitting Pack", "complete", "Splitting Pack Completed");

                const withholdingDocs = classifiedDocuments.filter(
                  (doc) => doc.doc_type === "Withholding Statement"
                );
                const otherDocs = classifiedDocuments.filter(
                  (doc) => doc.doc_type !== "Withholding Statement"
                );

                if (withholdingDocs.length === 0 && otherDocs.length > 0) {
                  excelFileName = selectedRowFromRows.fileImportId;
                  await processOtherDocuments(
                    otherDocs,
                    {},
                    excelFileName,
                    selectedRowFromRows,
                    statistics
                  );
                  return;
                }

                // Check if there are no documents at all
                if (withholdingDocs.length === 0 && otherDocs.length === 0) {
                  setIsPackProcessStarted(false);
                  setIsProcessing(false);
                  currentIndex++;
                  processNextItem();
                  return;
                }

                // Process Withholding Statement first
                for (let j = 0; j < withholdingDocs.length; j++) {
                  const withholdingDoc = withholdingDocs[j];
                  try {
                    const requestId = generateUUID();
                    excelFileName = selectedRowFromRows.fileImportId;
                    const displayName =
                      withholdingDocs.length > 1
                        ? `Extracting Withholding ${j + 1}`
                        : "Starting Withholding Statement Extraction";
                    const displayName2 =
                      withholdingDocs.length > 1
                        ? `Withholding ${j + 1} Extraction`
                        : "Withholding Statement Extraction";

                    addDocumentToProcess(
                      { output_path: `withholding-${j}-${withholdingDoc.output_path}` },
                      `${displayName}`
                    );

                    const uploadResponse = await uploadForm(
                      requestId,
                      withholdingDoc.output_path,
                      withholdingDoc.doc_type,
                      selectedRowFromRows.fileImportId,
                      true,
                      "Demo",
                      "",
                      selectedRowFromRows.organizationId,
                      selectedRowFromRows.userID
                    );

                    if (uploadResponse.status === "success") {
                      let newFileResult;
                      // Single WHS in pack: reuse the original fileImportId so the Excel
                      // written by the backend aligns with the existing DB record.
                      const whsRequestId =
                        withholdingDocs.length > 1
                          ? generateUUID()
                          : selectedRowFromRows.fileImportId;
                      if (withholdingDocs.length > 1) {
                        newFileResult = await createNewFileForMultipleWithholdings(
                          selectedRowFromRows,
                          whsRequestId,
                          j + 1
                        );
                      }

                      const newRow =
                        withholdingDocs.length > 1 ? newFileResult : selectedRowFromRows;
                      const formData = new FormData();
                      formData.append("filename", selectedRowFromRows.fileImportId);
                      formData.append("is_multiple_whs", withholdingDocs.length > 1 ? true : false);
                      formData.append("request_id", whsRequestId);
                      formData.append("business_unit", selectedRowFromRows.businessUnit);
                      const jwtUserWhs = getUserIdFromLocalStorage();
                      formData.append(
                        "organization_id",
                        selectedRowFromRows.organizationId || jwtUserWhs.organizationId || ""
                      );
                      formData.append(
                        "user_id",
                        selectedRowFromRows.userID || jwtUserWhs.userId || ""
                      );
                      formData.append("actual_filename", withholdingDoc.file_url);
                      formData.append("extension", "pdf");
                      formData.append("upload_type", String(selectedRowFromRows.uploadType || 11));
                      formData.append("original_filename", selectedRowFromRows.fileName);

                      const url = `${pythonBaseUrl}/Extract/Azure-Upload-File`;

                      const response = await fetch(url, {
                        method: "POST",
                        headers: {
                          Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
                        },
                        body: formData,
                        mode: "cors",
                      });

                      if (response.ok) {
                        const status = await pollFileStatus(
                          whsRequestId,
                          newRow,
                          selectedRowFromRows.businessUnit,
                          withholdingDocs.length > 1 ? selectedRowFromRows.fileImportId : null,

                          selectedRowFromRows.fileImportId
                        );

                        if (status === "FAILED") {
                          setSelectedRow("");
                          setNotifications((prev) => [
                            ...prev,
                            NotificationService.fileProcessingError(
                              selectedRowFromRows.fileName,
                              "File processing failed. Please try again."
                            ),
                          ]);

                          // Move to next item
                          currentIndex++;
                          setIsPackProcessStarted(false);
                          setIsProcessing(false);
                          processNextItem();
                          return;
                        } else if (status === "WHS_EMPTY") {
                          updateDocumentStatus(
                            `withholding-${j}-${withholdingDoc.output_path}`,
                            "failed",
                            `${displayName2} Failed`
                          );
                          // Don't push to processedWithholdingFiles — no data extracted
                          // Continue with other documents if this was the last WHS doc
                          if (j === withholdingDocs.length - 1) {
                            await processOtherDocuments(
                              otherDocs,
                              formTypeCounts,
                              excelFileName,
                              selectedRowFromRows,
                              statistics,
                              selectedRowFromRows.fileName
                            );
                          }
                        } else if (status === "DONE") {
                          processedWithholdingFiles.push(`${whsRequestId}.xlsx`);
                          setNotifications((prev) => [
                            ...prev,
                            NotificationService.fileProcessingComplete(
                              selectedRowFromRows.fileName,
                              selectedRowFromRows.fileImportId,
                              "File extraction completed successfully!"
                            ),
                          ]);

                          updateDocumentStatus(
                            `withholding-${j}-${withholdingDoc.output_path}`,
                            "complete",
                            `${displayName2} Completed`
                          );

                          if (j === withholdingDocs.length - 1) {
                            if (withholdingDocs.length > 1) {
                              excelFileName = selectedRowFromRows.fileImportId;
                              const formData = new FormData();
                              formData.append(
                                "filename",
                                selectedRowFromRows.fileImportId // Use new request ID
                              );
                              formData.append("organizationId", selectedRowFromRows.organizationId);
                              formData.append("userId", selectedRowFromRows.userID);
                              formData.append(
                                "businessUnit",
                                selectedRowFromRows.businessUnit || "Demo"
                              );
                              formData.append(
                                "uploadType",
                                String(selectedRowFromRows.uploadType || 11)
                              );
                              formData.append("request_id", whsRequestId);

                              processedWithholdingFiles.forEach((path) => {
                                formData.append("fileList", path);
                              });

                              try {
                                const withHoldingApiUrl = `${pythonBaseUrl}/Excel/MergeExcels`;

                                addDocumentToProcess(
                                  {
                                    output_path:
                                      "Validating all withholdings & consolidating them into one.",
                                  },
                                  `Validating all withholdings & consolidating them into one.`
                                );

                                const apiResponse = await fetch(withHoldingApiUrl, {
                                  method: "POST",
                                  headers: {
                                    Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
                                  },
                                  body: formData,
                                });

                                if (apiResponse.ok) {
                                  updateDocumentStatus(
                                    "Validating all withholdings & consolidating them into one.",
                                    "complete",
                                    `Validating all withholdings & consolidating them into one.`
                                  );

                                  await processOtherDocuments(
                                    otherDocs,
                                    formTypeCounts,
                                    excelFileName,
                                    selectedRowFromRows,
                                    statistics,
                                    selectedRowFromRows.fileName
                                  );
                                } else {
                                  throw new Error("Failed to send withholding files list to API");
                                }
                              } catch (apiError) {
                                console.error("Error sending withholding files to API:", apiError);
                                setIsPackProcessStarted(false);
                                setIsProcessing(false);
                                currentIndex++;
                                processNextItem();
                                return;
                              }
                            } else {
                              await processOtherDocuments(
                                otherDocs,
                                formTypeCounts,
                                excelFileName,
                                selectedRowFromRows,
                                statistics,
                                selectedRowFromRows.fileName
                              );
                            }
                          }
                        }
                      } else {
                        throw new Error("Azure upload failed");
                      }
                    } else {
                      throw new Error("Upload form failed");
                    }
                  } catch (error) {
                    console.error("Error processing Withholding Statement:", error);
                    setIsPackProcessStarted(false);
                    setIsProcessing(false);
                    currentIndex++;
                    processNextItem();
                    return;
                  }
                }
                // if (withholdingDocs.length === 1) {
                //   handleDelete(selectedRowFromRows.fileImportId);
                // }
              } else {
                throw new Error("Split status polling failed");
              }
            } catch (error) {
              console.error("Polling failed:", error);
              setIsPackProcessStarted(false);
              setIsProcessing(false);
              currentIndex++;
              processNextItem();
            }
          } else {
            // Non-pack processing
            if (response.ok) {
              const status = await pollFileStatus(
                selectedRowFromRows.fileImportId,
                selectedRowFromRows
              );

              if (status === "FAILED") {
                setSelectedRow("");
                toast.error(
                  `Extraction failed for "${selectedRowFromRows.fileName}". The extraction service returned an error. Please try again.`,
                  { autoClose: 7000, position: "top-center" }
                );
                setNotifications((prev) => [
                  ...prev,
                  NotificationService.fileProcessingError(
                    selectedRowFromRows.fileName,
                    "File processing failed. Please try again."
                  ),
                ]);
              } else if (status === "WHS_EMPTY") {
                setSelectedRow("");
                toast.error(
                  `WHS extraction completed for "${selectedRowFromRows.fileName}" but no data was found.`,
                  { autoClose: 7000, position: "top-center" }
                );
              } else if (status === "DONE") {
                logFileProcess({
                  fileImportId: selectedRowFromRows.fileImportId,
                  fileName: selectedRowFromRows.fileName,
                  extractedDate: new Date(),
                });

                setNotifications((prev) => [
                  ...prev,
                  NotificationService.fileProcessingComplete(
                    selectedRowFromRows.fileName,
                    selectedRowFromRows.fileImportId,
                    "File extraction completed successfully!"
                  ),
                ]);

                setSuccessFilename(selectedRowFromRows.fileName);
                setIsSuccessPopup(true);
              }
            } else {
              setSelectedRow("");
              toast.error(
                `Upload failed for "${selectedRowFromRows.fileName}". The extraction service could not process the file. Please try again.`,
                { autoClose: 7000, position: "top-center" }
              );
              setNotifications((prev) => [
                ...prev,
                NotificationService.fileProcessingError(
                  selectedRowFromRows.fileName,
                  "File processing failed. Please try again."
                ),
              ]);

              console.error(`Azure AI API call failed for fileId: ${rowId}`);
            }

            // Proceed to next item
            currentIndex++;
            processNextItem();
          }
        } catch (error) {
          console.error("Error processing row:", error);
          currentIndex++;
          processNextItem();
        }
      };

      // Helper function to process other documents
      const processOtherDocuments = async (
        otherDocs,
        formTypeCounts,
        excelFileName,
        selectedRowFromRows,
        statistics,
        original_filename
      ) => {
        addDocumentToProcess(
          { output_path: "Starting Form Extraction" },
          `Starting Form Extraction`
        );

        await new Promise((resolve) => setTimeout(resolve, 2000));

        updateDocumentStatus("Starting Form Extraction", "complete", `Starting Form Extraction`);

        // Process other documents after Withholding Statement
        for (let i = 0; i < otherDocs.length; i++) {
          const doc = otherDocs[i];

          try {
            const docRequestId = generateUUID();
            const formType = getFormTypeDisplayName(doc.doc_type);

            // Initialize or increment counter for this form type
            formTypeCounts[formType] = (formTypeCounts[formType] || 0) + 1;

            // Create a display name with number if needed
            const formTypeDisplay =
              formTypeCounts[formType] > 1 ? `${formType} ${formTypeCounts[formType]}` : formType;

            addDocumentToProcess(
              { output_path: `form-${i}-${doc.output_path}` },
              `Extracting ${formTypeDisplay}`
            );

            const docUploadResponse = await uploadForm(
              docRequestId,
              doc.file_url,
              doc.doc_type,
              excelFileName,
              false,
              selectedRowFromRows.businessUnit || "Demo",
              original_filename,
              selectedRowFromRows.organizationId,
              selectedRowFromRows.userID
            );

            if (docUploadResponse.status === "success") {
              if (docUploadResponse.data != null) {
                setFormsdata((prevFormsData) => [...prevFormsData, docUploadResponse.data]);
              }

              updateDocumentStatus(
                `form-${i}-${doc.output_path}`,
                "complete",
                `${formTypeDisplay} Extraction Completed`
              );

              // Check if it's the last item
              if (i === otherDocs.length - 1) {
                await updateExcel(selectedRowFromRows, formsData, statistics);
                setSuccessFilename(selectedRowFromRows.fileName);
                setIsSuccessPopup(true);

                // Move to next item in the main queue
                setIsPackProcessStarted(false);
                setIsProcessing(false);
                currentIndex++;
                processNextItem();
              }
            } else {
              updateDocumentStatus(
                `form-${i}-${doc.output_path}`,
                "error",
                `${doc.doc_type} Extraction Failed`
              );
            }
          } catch (error) {
            updateDocumentStatus(
              `form-${i}-${doc.output_path}`,
              "error",
              `${doc.doc_type} extraction failed`
            );
          }
        }
      };

      // Start processing the first item
      processNextItem();
    }
  };

  const updateExcel = async (rowData, formsData, statistics) => {
    try {
      addDocumentToProcess(
        { output_path: "Generating Excel & XML files" },
        `Generating Excel & XML files`
      );

      const formData = new FormData();
      formData.append("filename", rowData.fileImportId);
      const jwtUserExcel = getUserIdFromLocalStorage();
      formData.append(
        "organization_id",
        rowData.organizationId || jwtUserExcel.organizationId || ""
      );
      formData.append("user_id", rowData.userID || jwtUserExcel.userId || "");
      formData.append("business_unit", rowData.businessUnit || "Demo");
      formData.append("upload_type", String(rowData.uploadType || 11));
      formData.append("original_filename", rowData.fileName);

      const uploadResponse = await fetch(`${pythonBaseUrl}/Excel/Import-Excel-File`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
        },
        body: formData,
      });

      // Check if the response is successful
      if (uploadResponse.ok) {
        const responseData = await uploadResponse.json();

        const totalFieldsWithValues = countFieldsWithValues(responseData.extractedValidationData);

        const updatedRowData = {
          ...rowData,
          isProcessed: true,
          formsData: JSON.stringify(formsData),
          dataExtraction: JSON.stringify(responseData.tableData),
          dataValidation: JSON.stringify(responseData.extractedValidationData),
          dataVisualisation: JSON.stringify(responseData.treeData),
          numberOfFields: totalFieldsWithValues,
          confidenceLevel: responseData.confidence_score
            ? responseData.confidence_score < 1
              ? Math.round(responseData.confidence_score * 100)
              : Math.round(responseData.confidence_score)
            : !rowData.confidenceLevel
              ? 98
              : rowData.confidenceLevel,
          businessUnit: !rowData.businessUnit ? "Demo" : rowData.businessUnit,
          totalForms: rowData.totalForms
            ? rowData.totalForms
            : rowData.uploadType === 1
              ? 0
              : statistics.total_forms,
          totalPages: rowData.totalPages ? rowData.totalPages : statistics.total_pages,
          status: 2, // Status 2: Extraction complete
        };

        updateDocumentStatus(
          "Generating Excel & XML files",
          "complete",
          `Generating Excel & XML files Complete`
        );
        logFileProcess({
          fileImportId: updatedRowData.fileImportId,
          fileName: updatedRowData.fileName,
          extractedDate: new Date(),
        });
        const updateResponse = await fetch(`${BASE_URL}/FileData/ImportFiles`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
          },
          body: JSON.stringify(updatedRowData),
        });

        if (!updateResponse.ok) {
          throw new Error("Failed to update file data");
        } else {
          setWithHolding(updatedRowData);
        }

        return true; // Indicates success
      } else {
        const errorData = await uploadResponse.json();
        console.error("Error Response:", errorData);
        updateDocumentStatus(
          "Generating Excel & XML files",
          "error",
          `Excel & XML Generation Failed`
        );
        return false; // Indicates failure
      }
    } catch (error) {
      console.error("Excel Update Error:", error);
      updateDocumentStatus(
        "Generating Excel & XML files",
        "error",
        `Excel & XML Generation Failed: ${error.message}`
      );
      return false; // Indicates failure
    }
  };

  // Build report payloads in a consistent way
  const buildClassificationReport = ({
    fileImportId,
    totals = {},
    taxForms = "",
    modelName = "",
    classifiedDocuments = [],
  }) => ({
    requestId: String(fileImportId || ""),
    subRequestId: String(fileImportId || ""),
    totalPages: Number(totals.total_pages || 0),
    totalForms: Number(totals.total_forms || 0),
    totalExtracted: Number(totals.total_extracted || 0),
    totalSplitted: Number(totals.total_splitted || totals.total_pages || 0),
    taxForms: String(taxForms || ""),
    otherDocument: "",
    json: classifiedDocuments.length > 0 ? JSON.stringify(classifiedDocuments) : "{}",
    type: "classification",
    modelName: String(modelName || ""),
  });

  const storeReports = async (reportData) => {
    try {
      const response = await fetch(`${BASE_URL}/FileImportReport/Report`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
          "Content-Type": "application/json",
          accept: "*/*",
        },
        body: JSON.stringify(reportData),
      });

      if (!response.ok) {
        throw new Error(`Failed with status: ${response.status}`);
      }

      // Try to parse as JSON first, fallback to text if it fails
      let data;
      const contentType = response.headers.get("content-type");

      try {
        if (contentType && contentType.includes("application/json")) {
          data = await response.json();
        } else {
          // If not JSON content type, get as text
          data = await response.text();
          console.log("Store report response (text):", data);
        }
      } catch (parseError) {
        // If JSON parsing fails, try as text
        console.warn("Failed to parse as JSON, trying as text:", parseError.message);
        data = await response.text();
        console.log("Store report response (fallback text):", data);
      }

      return { success: true, data };
    } catch (error) {
      console.error("Failed to store report:", error);
      return { success: false, error };
    }
  };

  function countFieldsWithValues(data) {
    let totalFieldsWithValues = 0;

    // Helper function to count non-empty values in an object
    const countNonEmptyFields = (obj) => {
      return Object.entries(obj).reduce((count, [_, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          return count + 1;
        }
        return count;
      }, 0);
    };

    // Count fields in accountConnectionsList
    if (data.accountConnectionsList) {
      data.accountConnectionsList.forEach((item) => {
        totalFieldsWithValues += countNonEmptyFields(item);
      });
    }

    // Count fields in accountsList
    if (data.accountsList) {
      data.accountsList.forEach((item) => {
        totalFieldsWithValues += countNonEmptyFields(item);
      });
    }

    // Count fields in businessUnitList
    if (data.businessUnitList) {
      data.businessUnitList.forEach((item) => {
        totalFieldsWithValues += countNonEmptyFields(item);
      });
    }

    return totalFieldsWithValues;
  }

  const uploadForm = async (
    requestId,
    filePath,
    docType,
    filename,
    isWithholding = false,
    businessUnit = "Demo",
    original_filename = "",
    organizationId = null,
    userId = null
  ) => {
    const formData = new FormData();
    formData.append("request_id", requestId);
    formData.append("filepath", filePath || "");
    formData.append("doc_type", docType);
    formData.append("filename", filename);
    formData.append("original_filename", original_filename);
    formData.append("corelationID", generateUUID);
    const jwtUserUpload = getUserIdFromLocalStorage();
    formData.append("organization_id", organizationId || jwtUserUpload.organizationId || "");
    formData.append("user_id", userId || jwtUserUpload.userId || "");
    formData.append("business_unit", businessUnit);
    formData.append("upload_type", isWithholding ? "1" : "11"); // 1 for withholding, 11 for pack processing

    const url = `${pythonBaseUrl}/Extract/UploadExtraction`;

    // Function to attempt the upload
    const attemptUpload = async () => {
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
          },
          body: formData,
          mode: "cors",
        });

        if (response.ok) {
          const jsonData = await response.json();

          // Report data is now stored directly by Python - no need to store from React

          if (isWithholding === true) {
            return { status: "success", data: null };
          } else {
            // Return the upload response directly instead of polling
            return { status: "success", data: jsonData };
          }
        } else {
          console.warn(`❌ Upload failed for ${docType} - Response not OK`);
          return { status: "failure", data: null };
        }
      } catch (error) {
        console.error(`💥 Error in uploadForm for ${docType}:`, error);
        return { status: "failure", error: error.message };
      }
    };

    // First attempt
    let result = await attemptUpload();

    // If first attempt fails, try one more time
    if (result.status === "failure") {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      result = await attemptUpload();
    }
    return result;
  };

  const handleSetValueDefaultBusinessunit = (value) => {
    setDefaultBusinessunit(value);
  };

  const handleSetValueDefaultOrganization = (value) => {
    setDefaultOrganization(value);
  };

  const handleSetValueDefaultUser = (value) => {
    setDefaultUser(value);
  };

  // Function to create a new file for multiple withholding statements
  const createNewFileForMultipleWithholdings = async (
    originalRow,
    newRequestId,
    withholdingCount
  ) => {
    try {
      const newFileData = {
        fileImportId: newRequestId, // Use new generated request ID
        fileName: `${originalRow.fileName}_ WHS_${withholdingCount}`,
        originalFileName: originalRow.fileName,
        businessUnit: originalRow.businessUnit,
        businessUnitID: originalRow.businessUnitID || originalRow.businessUnitId,
        businessUnitInstance: originalRow.businessUnitInstance,
        businessUnitAdminUrl: originalRow.businessUnitAdminUrl,
        uploadType: 1,
        isProcessed: false,
        isWithholding: true,
        uploadedBy: getUserIdFromLocalStorage()?.username || "System",
        uploadedDate: new Date().toISOString(),
        organizationName: getUserIdFromLocalStorage()?.organizationName || null,
        userID: originalRow.userID,
        parentId: originalRow.fileImportId, // Reference to original file
        status: null,
        notes: "",
        dataExtraction: null,
        dataValidation: null,
        dataVisualisation: null,
        formsData: null,
        accuracyPercentage: null,
        confidenceLevel: originalRow.confidenceLevel,
        numberOfFields: null,
        totalForms: originalRow.totalForms,
        totalPages: originalRow.totalPages,
        withholdingData: null,
        approvedBy: "",
        approvedDate: null,
        reviewedBy: "",
        reviewedDate: null,
        rejectedBy: null,
        rejectedDate: null,
        rejectionReason: "",
        emailedTo: "",
        emailedDate: null,
      };

      // Create the new file via POST request using centralized service
      const importResult = await importFileData([newFileData]);

      if (importResult.success) {
        const createdFile = importResult.data;
        return createdFile[0]; // Return first element of the array
      } else {
        console.error(`❌ Failed to create new file:`);
        return {
          success: false,
          error: "Import failed",
        };
      }
    } catch (error) {
      console.error(`💥 Error creating new file:`, error);

      return {
        success: false,
        error: error.message,
      };
    }
  };

  const sharedValue = {
    isMenuOpen,
    updateDrawerMenu,
    searchValue,
    isDirectNavigation,
    updateSearchValue,
    isChatWindowVisible,
    isNotificationWindowVisible,
    dataRows,
    metricsList,
    isPackProcessStarted,
    treeView,
    isTourCompleted,
    handleIsRunProcessStarted,
    defaultBusinessunit,
    notifications,
    handleTourGuide,
    isProcessing,
    processStates,
    selectedRow,
    selectedRows,
    businessUnitsList,
    isRunProcessStarted,
    isSuccessPopup,
    successFilename,
    DataExtractProcess,
    getDataRows,
    getBusinessUnits,
    numberOfSelectedRows,
    handleNotificationList,
    updateRowNumberOfPages,
    updateDirectNavigation,
    handleProcessingDialogue,
    updateDisplayChatWindowValue,
    updateNotificationWindowValue,
    handleSelectedRows,
    handleIsSuccessPopup,
    handleCheckTourCompleted,
    handleTourCompleted,
    handleTreeView,
    handleSetValueDefaultBusinessunit,
    defaultOrganization,
    handleSetValueDefaultOrganization,
    handleSetValueDefaultUser,
    defaultUser,
    createNewFileForMultipleWithholdings,
    storeReports,
    formatBusinessUnitLabel,
    getBusinessUnitIdByName,
  };

  return <SharedValueContext.Provider value={sharedValue}>{children}</SharedValueContext.Provider>;
};

export const getUploadTypeDisplayName = (uploadType) => {
  switch (uploadType) {
    case 1:
      return "Single WHS";
    case 11:
      return "Pack with Forms";
    case 13:
      return "Excel Import";
    default:
      // Handle uploadType 2-10 as Single Form
      if (uploadType >= 2 && uploadType <= 10) {
        return "Single Form";
      }
      return "Unknown";
  }
};

// Function to get document type for single forms based on uploadType
export const getDocumentTypeByUploadType = (uploadType) => {
  switch (uploadType) {
    case 1:
      return "WHS";
    case 2:
      return "W-9";
    case 3:
      return "W-8BEN";
    case 4:
      return "W-8BEN-E";
    case 5:
      return "W-8IMY";
    case 6:
      return "W-8ECI";
    case 7:
      return "W-8EXP";
    case 8:
      return "8233";
    case 9:
      return "SELFCERT-IND";
    case 10:
      return "SELFCERT-ENT";
    case 11:
      return "Pack With Forms";
    case 14:
      return "Forms Pack";
    default:
      return null;
  }
};

// Password Validation
export const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
export const intValue = (value, defaultValue = 0) => {
  const result = parseInt(value, 10); // Base 10 for decimal numbers
  return isNaN(result) ? 0 : result;
};

// src/colors.js
const ComplyColors = {
  primary: "#2aa7e0",
  background: "#E8EAEC",
  surfaceLight: "#E9F5FE",
  button_background: "#EAF6FC",
  white: "white",
  black: "#000000",
  fontColor: "#424242",
  checkboxColor: "#2aa7e0",
  // Add more colors as needed
};

export default ComplyColors;

export const paddingAndMargin = {
  sideMenuPadding: 30,
  screenMargin: 20,
  screenBorderRadius: 20,
  buutonBorderradius: 8,
  headerTopHeight: 15,
  headerBelowHeight: 15,
  // Add more padding and margin values as needed
};

export const screenHeaderStyle = (value) =>
  value === true
    ? "screen-header screen-header--menu-open"
    : "screen-header screen-header--menu-closed";

export const accurancyElement = (value) => (
  <div>
    <span
      className={`const-accuracy-text ${
        value === "Good"
          ? "const-accuracy-good"
          : value === "Okay"
            ? "const-accuracy-okay"
            : "const-accuracy-bad"
      }`}
    >
      {value}
    </span>
  </div>
);
export const DeleteButton = ({ onClick, disabled = false }) => (
  <img
    title={disabled ? "Delete (User is inactive)" : "Delete"}
    onClick={disabled ? undefined : onClick}
    src={trashSvg}
    alt="Delete"
    className={`const-delete-btn-img${disabled ? " const-delete-btn-img--disabled" : ""}`}
  />
);

export const EditButton = ({ onClick }) => (
  <img
    title="Update Data"
    onClick={onClick}
    src={editSvg}
    alt="Edit"
    className="const-edit-btn-img"
  />
);
export const ExpandButton = ({ onClick, topMargin, title }) => (
  <FullscreenIcon
    titleAccess="Expand"
    onClick={onClick}
    title={title}
    className="const-expand-btn"
    style={{ marginRight: topMargin }}
  />
);
export const MoveButton = ({ onClick }) => (
  <img
    title="Move Window"
    onClick={onClick}
    src="/Move.png"
    alt="Move"
    className="const-move-btn-img"
  />
);
export const UpperLowerText = ({ text }) =>
  text === "UID" || text === "XML"
    ? text // Return the text "as it is" if it is exactly "UID" or "XML"
    : text
        .split(" ") // Split the text into an array of words
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
        .join(" "); // Join the words back into a single string
// ─── AppCheckbox ─────────────────────────────────────────────────────────────

export const EyeButton = ({ onClick }) => (
  <img
    title="Update Data"
    onClick={onClick}
    src="/Eye.png"
    alt="Hide/Show"
    className="const-eye-btn-img"
  />
);

/**
 * AppCheckbox – reusable checkbox styled to the design spec.
 * 20×20px · 6px radius · 1px #D0D5DD border · white bg · #2aa7e0 when checked
 *
 * Props mirror the common MUI <Checkbox> API so call-sites are drop-in replaceable:
 *   checked, onChange, onClick, disabled, title, style (merged onto wrapper), indeterminate
 */
export const AppCheckbox = ({
  checked = false,
  onChange,
  onClick,
  disabled = false,
  title,
  style,
  indeterminate = false,
  header = false,
}) => {
  const isCheckedOrIndeterminate = checked || indeterminate;
  const uncheckedBg = header ? "#F7FAFF" : "#ffffff";
  const uncheckedBorder = header ? "var(--checkbox-active-color)" : "#D0D5DD";
  const handleClick = (e) => {
    if (disabled) return;
    e.stopPropagation();
    if (onChange) {
      const syntheticEvent = {
        type: "change",
        target: { checked: !checked },
        stopPropagation: () => {},
        preventDefault: () => {},
      };
      onChange(syntheticEvent);
    }
    if (onClick) onClick(e);
  };
  return (
    <span
      title={title}
      onClick={handleClick}
      className={`const-app-checkbox${disabled ? " const-app-checkbox--disabled" : ""}`}
      style={{
        border: `1px solid ${isCheckedOrIndeterminate ? "var(--checkbox-active-color)" : uncheckedBorder}`,
        backgroundColor: isCheckedOrIndeterminate ? "var(--checkbox-active-color)" : uncheckedBg,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange || (() => {})}
        disabled={disabled}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none" }}
      />
      {indeterminate && !checked ? (
        <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
          <rect x="0" y="0" width="10" height="2" rx="1" fill="white" />
        </svg>
      ) : checked ? (
        <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
          <path
            d="M1 4l3.5 3.5L11 1"
            stroke="white"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : header ? (
        <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
          <rect
            x="0"
            y="0"
            width="10"
            height="2"
            rx="1"
            style={{ fill: "var(--checkbox-active-color)" }}
          />
        </svg>
      ) : null}
    </span>
  );
};
export const fontFamily = "Poppins, sans-serif";

export const loginButtonStyle = "login-button-style";
export const loginButtonWithoutColorStyle = "login-button-outline-style";

export const paginationstyle = {
  marginRight: 30,
  marginLeft: "auto",
  display: "flex",
  marginTop: "auto",
  alignItems: "flex-end",
  marginBottom: 30,
};

// Menu Item Style
export const menuItemStyle = (selectedMenu, title) =>
  selectedMenu === title ? "menu-item-text menu-item-text--active" : "menu-item-text";

// Keep the original constant with default values
export const searchBoxStyle = "search-box-style";
export const forgotSearchBoxStyle = "forgot-search-box-style";
export const registrationTextFieldClassName = "registration-text-field-style";
export const registrationTextFieldStyle = {
  fontSize: "13px",
  background: ComplyColors.button_background,
  border: "none",
  paddingLeft: 10,
  width: 250,
  marginRight: "auto",
  marginLeft: "auto",
  height: 45,
  fontFamily: fontFamily,
  // marginBottom:20,
  paddingRight: 10,
  borderRadius: "11px",
  color: "black",
};

export const CustomModal = ({ open, onClose, title, description }) => {
  return (
    <Modal
      open={open}
      onClose={onClose || null}
      disableBackdropClick={true}
      aria-labelledby="custom-modal-title"
      aria-describedby="custom-modal-description"
      className="const-modal-overlay"
    >
      <div className="const-modal-content">
        {/* Close Button */}
        <IconButton onClick={onClose} className="const-modal-close-btn">
          <CloseIcon />
        </IconButton>

        <div className="const-modal-body">
          {/* Access Denied Icon */}
          <ShieldAlert size={40} color="#e65c00" strokeWidth={1.8} style={{ marginBottom: 8 }} />
          {title && <h6 style={{ color: "#2aa7e0", marginBottom: 15 }}>{title}</h6>}
        </div>

        <div className="const-modal-desc">
          <div className="const-modal-desc-inner">{description}</div>
        </div>
      </div>
    </Modal>
  );
};

export const countries = [
  "Afghanistan",
  "Akrotiri",
  "Aland Islands",
  "Albania",
  "Algeria",
  "American Samoa",
  "Andorra",
  "Angola",
  "Anguilla",
  "Antarctica",
  "Antigua & Barbuda",
  "Argentina",
  "Armenia",
  "Aruba",
  "Ashmore and Cartier Islands",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Baker Island",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bermuda",
  "Bhutan",
  "Bolivia",
  "Bonaire, Sint Eustatius and Saba",
  "Bosnia-Herzegovina",
  "Botswana",
  "Bouvet Island",
  "Brazil",
  "British Indian Ocean Territory",
  "British Virgin Islands",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Cape Verde",
  "Cayman Islands",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Christmas Island",
  "Clipperton Island",
  "Cocos (Keeling) Islands",
  "Colombia",
  "Comoros",
  "Congo (Brazzaville)",
  "Congo (Kinshasa)",
  "Coral Sea Islands",
  "Costa Rica",
  "Cote D'Ivoire (Ivory Coast)",
  "Croatia",
  "Cuba",
  "Curacao",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Dhekelia",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "East Timor",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Ethiopia",
  "Falkland Islands (Islas Malvinas)",
  "Faroe Islands",
  "Federated States of Micronesia",
  "Fiji",
  "Finland",
  "France",
  "French Guiana",
  "French Metropolitan",
  "French Polynesia",
  "French Southern and Antarctic Lands",
  "Gabon",
  "Georgia",
  "Germany",
  "Ghana",
  "Gibraltar",
  "Greece",
  "Greenland",
  "Grenada",
  "Guadeloupe",
  "Guam",
  "Guatemala",
  "Guernsey",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Heard Island and McDonald Islands",
  "Holy See",
  "Honduras",
  "Hong Kong",
  "Howland Island",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Isle Of Man",
  "Israel",
  "Italy",
  "Jamaica",
  "Jan Mayen",
  "Japan",
  "Jarvis Island",
  "Jersey",
  "Johnston Atoll",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kingman Reef",
  "Kiribati",
  "Korea, Democratic People's Republic of (North)",
  "Korea, Republic of (South)",
  "Kosovo",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Macau",
  "Macedonia",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Martinique",
  "Mauritania",
  "Mauritius",
  "Mayotte",
  "Mexico",
  "Midway Islands",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Montserrat",
  "Morocco",
  "Mozambique",
  "Myanmar (Burma)",
  "Namibia",
  "Nauru",
  "Navassa Island",
  "Nepal",
  "Netherlands",
  "Netherlands Antilles",
  "New Caledonia",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "Niue",
  "Norfolk Island",
  "Northern Mariana Islands",
  "Norway",
  "Oman",
  "Other",
  "Pakistan",
  "Palau",
  "Palestine, State of",
  "Panama",
  "Papua-New Guinea",
  "Paracel Islands",
  "Paraguay",
  "Peru",
  "Philippines",
  "Pitcairn Islands",
  "Poland",
  "Portugal",
  "Puerto Rico",
  "Qatar",
  "Reunion",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Barthelemy",
  "Saint Martin",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Sint Maarten",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Georgia and the South Sandwich Islands",
  "South Sudan",
  "Spain",
  "Spratly Islands",
  "Sri Lanka",
  "St. Helena",
  "St. Kitts And Nevis",
  "St. Lucia Island",
  "St. Pierre and Miquelon",
  "St. Vincent and the Grenadines",
  "Sudan",
  "Suriname",
  "Svalbard",
  "Swaziland",
  "Sweden",
  "Switzerland",
  "Syria",
  "Taiwan",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "The Gambia",
  "Togo",
  "Tokelau",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Turks And Caicos Islands",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "United States Minor Outlying Islands",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican City State",
  "Venezuela",
  "Vietnam",
  "Virgin Islands (U.S.)",
  "Wake Island",
  "Wallis and Futuna",
  "Western Sahara",
  "Yemen",
  "Yugoslavia",
  "Zambia",
  "Zimbabwe",
];

export const EXTRACT_NO_SELECTION_MESSAGE = "Please select at least one file for extracting data.";
